import { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
} from '@mui/material';
import intl from 'components/i18n/ReactIntlWrapper';
import { InvoiceConfiguration } from './InvoiceConfigurationPage';

type InvoiceConfigurationDrawerProps = {
  open: boolean;
  onClose: () => void;
  onSave: (config: Partial<InvoiceConfiguration>) => void;
  config: InvoiceConfiguration | null;
  corporateEntityId: number | null;
  countryCode?: string; // Optional: if provided, country will be locked to this value
};

const COUNTRY_CODES = [
  { code: 'US', name: 'United States' },
  { code: 'AR', name: 'Argentina' },
  { code: 'BR', name: 'Brazil' },
  { code: 'CA', name: 'Canada' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'MX', name: 'Mexico' },
  { code: 'UY', name: 'Uruguay' },
];

const NUMBER_FORMATS = [
  { value: 'D4', label: '0001 (4 digits)' },
  { value: 'D5', label: '00001 (5 digits)' },
  { value: 'D6', label: '000001 (6 digits)' },
  { value: 'D7', label: '0000001 (7 digits)' },
  { value: 'D8', label: '00000001 (8 digits)' },
];

const InvoiceConfigurationDrawer: React.FC<InvoiceConfigurationDrawerProps> = ({
  open,
  onClose,
  onSave,
  config,
  corporateEntityId,
  countryCode,
}) => {
  const [formData, setFormData] = useState<Partial<InvoiceConfiguration>>({
    countryCode: 'US',
    prefix: '',
    numberFormat: 'D8',
    separator: '-',
    startingNumber: 1,
    active: true,
    additionalSettings: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (config) {
      // Editing existing configuration
      setFormData({
        countryCode: config.countryCode,
        prefix: config.prefix || '',
        numberFormat: config.numberFormat,
        separator: config.separator,
        startingNumber: config.startingNumber,
        active: config.active,
        additionalSettings: config.additionalSettings || '',
      });
    } else {
      // Creating new configuration - use provided countryCode or default to US
      setFormData({
        countryCode: countryCode || 'US',
        prefix: '',
        numberFormat: 'D8',
        separator: '-',
        startingNumber: 1,
        active: true,
        additionalSettings: '',
      });
    }
    setErrors({});
  }, [config, countryCode, open]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.countryCode || formData.countryCode.length !== 2) {
      newErrors.countryCode = intl.get('invoiceConfig.error.countryCodeRequired');
    }

    if (!formData.numberFormat) {
      newErrors.numberFormat = intl.get('invoiceConfig.error.numberFormatRequired');
    }

    if (!formData.startingNumber || formData.startingNumber < 1) {
      newErrors.startingNumber = intl.get('invoiceConfig.error.startingNumberInvalid');
    }

    // Validate JSON if additionalSettings is provided
    if (formData.additionalSettings) {
      try {
        JSON.parse(formData.additionalSettings);
      } catch (e) {
        newErrors.additionalSettings = intl.get('invoiceConfig.error.invalidJson');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }

    onSave(formData);
  };

  const handleChange = (field: keyof InvoiceConfiguration, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Generate preview
  const generatePreview = () => {
    const prefix = formData.prefix || '';
    const separator = formData.separator || '';
    const format = formData.numberFormat || 'D8';
    const padding = parseInt(format.replace('D', ''), 10) || 8;
    const example = formData.startingNumber || 1;
    const formattedNumber = String(example).padStart(padding, '0');
    return `${prefix}${separator}${formattedNumber}`;
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 500, p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          {config
            ? intl.get('invoiceConfig.editConfiguration')
            : intl.get('invoiceConfig.addConfiguration')}
        </Typography>

        {!config && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {intl.get('invoiceConfig.newConfigHelp')}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {countryCode ? (
            // If countryCode is locked by parent (Corporate Entity), show as read-only
            <TextField
              fullWidth
              label={intl.get('invoiceConfig.field.countryCode')}
              value={`${formData.countryCode} - ${COUNTRY_CODES.find(c => c.code === formData.countryCode)?.name || formData.countryCode}`}
              disabled
              helperText={intl.get('invoiceConfig.field.countryCodeLocked')}
            />
          ) : (
            // Otherwise show dropdown selector
            <FormControl fullWidth error={!!errors.countryCode}>
              <InputLabel>{intl.get('invoiceConfig.field.countryCode')}</InputLabel>
              <Select
                value={formData.countryCode || ''}
                onChange={(e) => handleChange('countryCode', e.target.value)}
                label={intl.get('invoiceConfig.field.countryCode')}
                disabled={!!config} // Can't change country code after creation
              >
                {COUNTRY_CODES.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    {country.code} - {country.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.countryCode && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {errors.countryCode}
                </Typography>
              )}
            </FormControl>
          )}

          <TextField
            fullWidth
            label={intl.get('invoiceConfig.field.prefix')}
            value={formData.prefix || ''}
            onChange={(e) => handleChange('prefix', e.target.value)}
            error={!!errors.prefix}
            helperText={errors.prefix || intl.get('invoiceConfig.field.prefixHelp')}
            placeholder="INV, BERP, CR"
          />

          <FormControl fullWidth error={!!errors.numberFormat}>
            <InputLabel>{intl.get('invoiceConfig.field.numberFormat')}</InputLabel>
            <Select
              value={formData.numberFormat || 'D8'}
              onChange={(e) => handleChange('numberFormat', e.target.value)}
              label={intl.get('invoiceConfig.field.numberFormat')}
            >
              {NUMBER_FORMATS.map((format) => (
                <MenuItem key={format.value} value={format.value}>
                  {format.label}
                </MenuItem>
              ))}
            </Select>
            {errors.numberFormat && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {errors.numberFormat}
              </Typography>
            )}
          </FormControl>

          <TextField
            fullWidth
            label={intl.get('invoiceConfig.field.separator')}
            value={formData.separator || ''}
            onChange={(e) => handleChange('separator', e.target.value)}
            error={!!errors.separator}
            helperText={errors.separator || intl.get('invoiceConfig.field.separatorHelp')}
            placeholder="-"
          />

          <TextField
            fullWidth
            type="number"
            label={intl.get('invoiceConfig.field.startingNumber')}
            value={formData.startingNumber || 1}
            onChange={(e) => handleChange('startingNumber', parseInt(e.target.value, 10))}
            error={!!errors.startingNumber}
            helperText={errors.startingNumber || intl.get('invoiceConfig.field.startingNumberHelp')}
            inputProps={{ min: 1 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.active ?? true}
                onChange={(e) => handleChange('active', e.target.checked)}
              />
            }
            label={intl.get('invoiceConfig.field.active')}
          />

          <TextField
            fullWidth
            multiline
            rows={4}
            label={intl.get('invoiceConfig.field.additionalSettings')}
            value={formData.additionalSettings || ''}
            onChange={(e) => handleChange('additionalSettings', e.target.value)}
            error={!!errors.additionalSettings}
            helperText={
              errors.additionalSettings || intl.get('invoiceConfig.field.additionalSettingsHelp')
            }
            placeholder='{"CUIT":"20123456789","PuntoVenta":1}'
          />

          {/* Preview */}
          <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {intl.get('invoiceConfig.preview')}:
            </Typography>
            <Typography variant="h6" sx={{ fontFamily: 'monospace', color: 'primary.main' }}>
              {generatePreview()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {intl.get('invoiceConfig.previewHelp')}
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
            <Button onClick={onClose} variant="outlined">
              {intl.get('common.cancel')}
            </Button>
            <Button onClick={handleSave} variant="contained">
              {intl.get('common.save')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default InvoiceConfigurationDrawer;
